import {
  useListDevices,
  useUpdateDeviceState,
  getListDevicesQueryKey,
  getGetDeviceQueryKey,
  DevicesAvailabilityState,
  type DevicesDevice,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronDown, Grid3x3, X } from 'lucide-react';
import { memo, useCallback, useState } from 'react';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

import { getDeviceIcon, getDeviceOn, applyStatePatch } from '@/features/devices/utils';
import { getDeviceIdForApi } from '@/features/devices/utils/deviceState';

/**
 * Devices widget config schema
 */
export const devicesConfigSchema = z.object({
  deviceHumanIds: z.array(z.string()).min(1, 'At least one device is required'),
});

export type DevicesConfig = z.infer<typeof devicesConfigSchema>;

/**
 * Multi-device selector component
 */
function DeviceMultiSelect({
  selectedIds,
  onChange,
  error,
}: {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { data: devicesData, isLoading } = useListDevices();
  const devices = devicesData?.devices ?? [];

  const selectedDevices = devices.filter((d) => d.humanId && selectedIds.includes(d.humanId));

  const handleToggle = (device: DevicesDevice) => {
    if (!device.humanId) return;
    if (selectedIds.includes(device.humanId)) {
      onChange(selectedIds.filter((id) => id !== device.humanId));
    } else {
      onChange([...selectedIds, device.humanId]);
    }
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((i) => i !== id));
  };

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        Devices
      </label>

      {/* Selected devices chips */}
      {selectedDevices.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selectedDevices.map((device) => (
            <span
              key={device.humanId}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
            >
              {device.name || device.humanId}
              <button
                type="button"
                onClick={() => device.humanId && handleRemove(device.humanId)}
                className="rounded-full p-0.5 hover:bg-blue-200 dark:hover:bg-blue-500/30"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
            error
              ? 'border-red-300 bg-red-50/50 dark:border-red-500/30 dark:bg-red-500/10'
              : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-white/[0.12]'
          }`}
        >
          <span className="text-gray-500 dark:text-zinc-500">
            {selectedDevices.length > 0
              ? `${selectedDevices.length} device${selectedDevices.length > 1 ? 's' : ''} selected`
              : 'Select devices...'}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  Loading devices...
                </div>
              ) : devices.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-500">
                  No devices available
                </div>
              ) : (
                devices.map((device) => {
                  const DeviceIcon = getDeviceIcon(device.type, device.subtype);
                  const isOn = getDeviceOn(device);
                  return (
                    <button
                      key={device.id}
                      type="button"
                      onClick={() => handleToggle(device)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                    >
                      <div className="flex items-center gap-2">
                        <DeviceIcon
                          className={`h-4 w-4 ${isOn ? 'text-emerald-500' : 'text-gray-400 dark:text-zinc-500'}`}
                        />
                        <div>
                          <div className="text-gray-900 dark:text-white">
                            {device.name || device.humanId}
                          </div>
                          {device.name && device.humanId && (
                            <div className="text-xs text-gray-500 dark:text-zinc-500">
                              {device.humanId}
                            </div>
                          )}
                        </div>
                      </div>
                      {device.humanId && selectedIds.includes(device.humanId) && (
                        <Check className="h-4 w-4 text-blue-500" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Config form for Devices widget
 */
function DevicesConfigForm({ config, onChange, errors }: ConfigFormProps<DevicesConfig>) {
  return (
    <div className="space-y-4">
      <DeviceMultiSelect
        selectedIds={config.deviceHumanIds}
        onChange={(ids) => onChange({ ...config, deviceHumanIds: ids })}
        error={errors?.['deviceHumanIds']}
      />
    </div>
  );
}

/**
 * Individual device row in the list
 */
function DeviceRow({ device }: { device: DevicesDevice }) {
  const queryClient = useQueryClient();
  const [_error, setError] = useState<string | null>(null);

  const deviceId = getDeviceIdForApi(device);
  const DeviceIcon = getDeviceIcon(device.type, device.subtype);
  const isOn = getDeviceOn(device);
  const isAvailable = device.availability === DevicesAvailabilityState.AvailabilityAvailable;

  const { mutate: updateDeviceState, isPending } = useUpdateDeviceState({
    mutation: {
      onMutate: async ({ id, data: stateData }) => {
        setError(null);
        await queryClient.cancelQueries({ queryKey: getListDevicesQueryKey() });

        const previousData = queryClient.getQueryData<{ devices: DevicesDevice[] }>(
          getListDevicesQueryKey()
        );

        queryClient.setQueryData<{ devices: DevicesDevice[] }>(getListDevicesQueryKey(), (old) => {
          if (!old?.devices) return old;
          return {
            ...old,
            devices: old.devices.map((d) => {
              if (d.id === id || d.humanId === id) {
                return {
                  ...d,
                  state: applyStatePatch(d.state, stateData.state),
                };
              }
              return d;
            }),
          };
        });

        return { previousData };
      },
      onError: (_err, _vars, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(getListDevicesQueryKey(), context.previousData);
        }
        setError('Failed');
      },
      onSettled: (_data, _err, { id }) => {
        queryClient.invalidateQueries({ queryKey: getGetDeviceQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getListDevicesQueryKey() });
      },
    },
  });

  const handleToggle = useCallback(() => {
    if (!deviceId || !isAvailable) return;
    const newState: DevicesDeviceState = {
      light: { on: !isOn },
      tv: { on: !isOn },
      speaker: { on: !isOn },
      fan: { on: !isOn },
      switch: { on: !isOn },
    };
    updateDeviceState({ id: deviceId, data: { state: newState } });
  }, [deviceId, isAvailable, isOn, updateDeviceState]);

  return (
    <div
      className={`flex items-center justify-between rounded-lg px-3 py-2 ${
        isOn ? 'bg-emerald-50 dark:bg-emerald-500/10' : 'bg-gray-50 dark:bg-zinc-800/50'
      }`}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <DeviceIcon
          className={`h-4 w-4 flex-shrink-0 ${isOn ? 'text-emerald-500' : 'text-gray-400 dark:text-zinc-500'}`}
        />
        <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
          {device.name || device.humanId}
        </span>
        {!isAvailable && (
          <span className="flex-shrink-0 text-[10px] text-amber-600 dark:text-amber-400">
            Unavailable
          </span>
        )}
      </div>
      <button
        onClick={handleToggle}
        disabled={isPending || !isAvailable}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
          isOn
            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
            : 'bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600'
        } ${isPending ? 'animate-pulse' : ''} ${!isAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isPending ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <DeviceIcon className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

/**
 * Renderer for Devices widget
 */
const DevicesRenderer = memo(function DevicesRenderer({ config }: RendererProps<DevicesConfig>) {
  const { data: devicesData, isLoading } = useListDevices({
    query: {
      refetchInterval: 10000,
    },
  });

  const allDevices = devicesData?.devices ?? [];
  const selectedDevices = allDevices.filter(
    (d) => d.humanId && config.deviceHumanIds.includes(d.humanId)
  );
  const missingIds = config.deviceHumanIds.filter(
    (id) => !allDevices.some((d) => d.humanId === id)
  );

  if (config.deviceHumanIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No devices configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  const rowCount = selectedDevices.length + (missingIds.length > 0 ? 1 : 0);

  return (
    <div
      className="grid h-full gap-2"
      style={{ gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))` }}
    >
      {selectedDevices.map((device) => (
        <DeviceRow key={device.id} device={device} />
      ))}
      {missingIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-xs text-amber-700 dark:text-amber-400">
            {missingIds.length} device{missingIds.length > 1 ? 's' : ''} not found
          </span>
        </div>
      )}
    </div>
  );
});

/**
 * Devices widget definition
 */
export const devicesWidget: WidgetDefinition<DevicesConfig> = {
  type: 'devices',
  displayName: 'Devices List',
  description: 'Display and control multiple devices',
  icon: Grid3x3,
  defaultSize: { w: 3, h: 4 },
  minSize: { w: 2, h: 2 },
  maxSize: { w: 6, h: 8 },
  configSchema: devicesConfigSchema,
  defaultConfig: {
    deviceHumanIds: [],
  },
  ConfigForm: DevicesConfigForm,
  Renderer: DevicesRenderer,
};
